import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Button from "@/components/Base/Button";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Lucide from "@/components/Base/Lucide";
import Litepicker from "@/components/Base/Litepicker";
import { Menu } from "@/components/Base/Headless";
import axios from "axios";

type ProductType = { id: string; typeName: string; companyId?: string };
type Product = { id: string; name: string; sku?: string; type?: string; status: boolean; productImageUrl?: string | null; retailSellingPrice?: number | null };
type Distributor = { id: string; name: string; code?: string };

export default function AddStockInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyIdFromUrl = searchParams.get("companyId");

  const STORAGE_KEY_FILTER = "stockledger_stockInAdd_filter";
  const STORAGE_KEY_SORT = "stockledger_stockInAdd_sort";

  const loadSavedFilter = (): string[] => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_FILTER);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.every((x) => typeof x === "string") ? parsed : [];
    } catch {
      return [];
    }
  };

  const loadSavedSortOrder = (): string[] => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_SORT);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.every((x) => typeof x === "string") ? parsed : [];
    } catch {
      return [];
    }
  };

  const [distributorId, setDistributorId] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [receivedAt, setReceivedAt] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [refNo, setRefNo] = useState("");
  const [documentUrl, setDocumentUrl] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docError, setDocError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [loadingDistributors, setLoadingDistributors] = useState(false);

  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [exchangeQuantities, setExchangeQuantities] = useState<Record<string, number>>({});
  const [exchangeInputEnabled, setExchangeInputEnabled] = useState<Record<string, boolean>>({});
  const [expandedTypeIds, setExpandedTypeIds] = useState<Set<string>>(new Set());

  const [selectedProductTypeFilters, setSelectedProductTypeFilters] = useState<string[]>(loadSavedFilter);
  /** Custom display order of product type labels (1st, 2nd, 3rd...). Empty = default order. */
  const [productTypeCustomOrder, setProductTypeCustomOrder] = useState<string[]>(loadSavedSortOrder);

  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();

  const queryCompany = companyIdFromUrl ? `?companyId=${companyIdFromUrl}` : "";

  useEffect(() => {
    (async () => {
      try {
        setLoadingDistributors(true);
        const res = await axios.get(`${apiUrl}/distributors?pageSize=500${companyIdFromUrl ? `&companyId=${companyIdFromUrl}` : ""}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDistributors(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        console.error("Failed to fetch distributors", err);
      } finally {
        setLoadingDistributors(false);
      }
    })();
  }, [apiUrl, token, companyIdFromUrl]);


  // Load product types and products from Product table (GET /products) for quantity entry
  useEffect(() => {
    (async () => {
      try {
        setLoadingData(true);
        const params = new URLSearchParams();
        params.set("pageSize", "500");
        if (companyIdFromUrl) params.set("companyId", companyIdFromUrl);
        const typesParams = new URLSearchParams();
        typesParams.set("pageSize", "200");
        if (companyIdFromUrl) typesParams.set("companyId", companyIdFromUrl);
        const [typesRes, productsRes] = await Promise.all([
          axios.get(`${apiUrl}/product-types?${typesParams.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${apiUrl}/products?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setProductTypes(typesRes.data?.data ?? []);
        const list = productsRes.data?.data ?? [];
        const fromProductTable = Array.isArray(list) ? list : [];
        setProducts(fromProductTable.filter((p: Product) => p.status !== false));
      } catch (err) {
        console.error("Failed to load product types / Product table", err);
      } finally {
        setLoadingData(false);
      }
    })();
  }, [apiUrl, companyIdFromUrl, token]);

  const PRODUCT_TYPE_ORDER = ["Devices", "Refills", "Accessories"];

  const productsByType = useMemo(() => {
    const map = new Map<string, { label: string; products: Product[] }>();
    for (const pt of productTypes) {
      const list = products.filter((p) => (p.type || "").trim() === (pt.typeName || "").trim());
      if (list.length > 0) map.set(pt.id, { label: pt.typeName, products: list });
    }
    const other = products.filter((p) => {
      const t = (p.type || "").trim();
      return !productTypes.some((pt) => (pt.typeName || "").trim() === t);
    });
    if (other.length > 0) map.set("__other__", { label: "Other", products: other });
    return map;
  }, [productTypes, products]);

  const orderedProductTypes = useMemo(() => {
    const entries = Array.from(productsByType.entries());
    const ordered: [string, { label: string; products: Product[] }][] = [];
    for (const name of PRODUCT_TYPE_ORDER) {
      const found = entries.find(([, v]) => v.label.trim().toLowerCase() === name.toLowerCase());
      if (found) ordered.push(found);
    }
    for (const e of entries) {
      if (!PRODUCT_TYPE_ORDER.some((n) => n.toLowerCase() === e[1].label.trim().toLowerCase())) ordered.push(e);
    }
    return ordered;
  }, [productsByType]);

  const filteredAndSortedProductTypes = useMemo(() => {
    let list = orderedProductTypes;
    if (selectedProductTypeFilters.length > 0) {
      const set = new Set(selectedProductTypeFilters.map((s) => s.trim().toLowerCase()));
      list = list.filter(([, v]) => set.has(v.label.trim().toLowerCase()));
    }
    if (productTypeCustomOrder.length > 0) {
      const orderMap = new Map(productTypeCustomOrder.map((label, i) => [label.trim().toLowerCase(), i]));
      list = [...list].sort((a, b) => {
        const ia = orderMap.get(a[1].label.trim().toLowerCase()) ?? 1e9;
        const ib = orderMap.get(b[1].label.trim().toLowerCase()) ?? 1e9;
        return ia - ib;
      });
    }
    return list;
  }, [orderedProductTypes, selectedProductTypeFilters, productTypeCustomOrder]);

  /** Current display order for the Sort menu (custom order if set, else default). */
  const sortMenuOrder = useMemo(() => {
    if (productTypeCustomOrder.length > 0) {
      const inCustom = productTypeCustomOrder.filter((label) =>
        orderedProductTypes.some(([, v]) => v.label.trim().toLowerCase() === label.trim().toLowerCase())
      );
      const rest = orderedProductTypes
        .filter(([, v]) => !productTypeCustomOrder.some((c) => c.trim().toLowerCase() === v.label.trim().toLowerCase()))
        .map(([, v]) => v.label);
      return [...inCustom, ...rest];
    }
    return orderedProductTypes.map(([, v]) => v.label);
  }, [orderedProductTypes, productTypeCustomOrder]);

  const moveSortOrder = useCallback((index: number, direction: "up" | "down") => {
    setProductTypeCustomOrder((prev) => {
      const next = [...sortMenuOrder];
      const j = direction === "up" ? index - 1 : index + 1;
      if (j < 0 || j >= next.length) return prev.length > 0 ? prev : [];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  }, [sortMenuOrder]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_FILTER, JSON.stringify(selectedProductTypeFilters));
    } catch {
      // ignore
    }
  }, [selectedProductTypeFilters]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SORT, JSON.stringify(productTypeCustomOrder));
    } catch {
      // ignore
    }
  }, [productTypeCustomOrder]);

  useEffect(() => {
    if (loadingData || productTypes.length === 0) return;
    setExpandedTypeIds((prev) => {
      const next = new Set(prev);
      productsByType.forEach((_, id) => next.add(id));
      return next;
    });
  }, [loadingData, productsByType.size]);

  const showProductSection = true;

  const toggleType = useCallback((id: string) => {
    setExpandedTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setQuantity = useCallback((productId: string, value: number) => {
    setQuantities((prev) => ({ ...prev, [productId]: value >= 0 ? value : 0 }));
  }, []);
  const setExchangeQuantity = useCallback((productId: string, value: number) => {
    setExchangeQuantities((prev) => ({ ...prev, [productId]: value >= 0 ? value : 0 }));
  }, []);
  const toggleExchangeInput = useCallback((productId: string) => {
    setExchangeInputEnabled((prev) => {
      const enabled = !!prev[productId];
      const next = { ...prev, [productId]: !enabled };
      if (enabled) {
        setExchangeQuantities((qPrev) => ({ ...qPrev, [productId]: 0 }));
      }
      return next;
    });
  }, []);

  const handleDocumentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocError("");
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("document", file);
      const res = await axios.post(`${apiUrl}/upload/document`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      const url = res.data?.data?.url ?? res.data?.data?.key ?? "";
      setDocumentUrl(url);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) && err.response?.data?.message ? err.response.data.message : "Upload failed";
      setDocError(String(msg));
      setDocumentUrl("");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleRemoveDocument = () => {
    setDocumentUrl("");
    setDocError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");
    if (!distributorId.trim()) {
      setSubmitError("Please select a Distributor.");
      return;
    }
    if (!invoiceNo.trim()) {
      setSubmitError("Please enter Invoice No.");
      return;
    }
    if (!receivedAt.trim()) {
      setSubmitError("Please select date.");
      return;
    }

    if (!documentUrl.trim()) {
      setSubmitError("Please upload a document before submitting.");
      return;
    }

    const lines = Object.entries(quantities)
      .filter(([, qty]) => qty > 0)
      .map(([productId, quantity]) => ({ productId, quantity }));
    const exchangeLines = Object.entries(exchangeQuantities)
      .filter(([, qty]) => qty > 0)
      .map(([productId, quantity]) => ({ productId, quantity }));

    if (lines.length === 0 && exchangeLines.length === 0) {
      setSubmitError("Please enter at least one Stock IN or Stock EXCHANGE quantity.");
      return;
    }

    const payload: Record<string, unknown> = {
      stockInType: "New from distributor",
      receivedAt: (receivedAt.trim() ? new Date(receivedAt) : new Date()).toISOString(),
      documentUrl: documentUrl.trim() || undefined,
      distributorId: distributorId.trim(),
      invoiceNo: invoiceNo.trim(),
      lines,
      exchangeLines,
    };

    setSubmitting(true);
    try {
      await axios.post(
        `${apiUrl}/stock-in${queryCompany}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitSuccess("Stock IN saved successfully.");
      setDistributorId("");
      setInvoiceNo("");
      setRefNo("");
      setDocumentUrl("");
      setQuantities({});
      setExchangeQuantities({});
      setExchangeInputEnabled({});
      setTimeout(() => {
        navigate(companyIdFromUrl ? `/stock-in?companyId=${companyIdFromUrl}` : "/stock-in");
      }, 2000);
    } catch (err: unknown) {
      const data = axios.isAxiosError(err) ? err.response?.data : undefined;
      const msg = data?.message ?? data?.error ?? "Failed to save";
      setSubmitError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    const to = companyIdFromUrl ? `/stock-in?companyId=${companyIdFromUrl}` : "/stock-in";
    navigate(to);
  };

  const handleReset = () => {
    setDistributorId("");
    setInvoiceNo("");
    setReceivedAt(dayjs().format("YYYY-MM-DD"));
    setRefNo("");
    setDocumentUrl("");
    setSelectedProductTypeFilters([]);
    setProductTypeCustomOrder([]);
    setQuantities({});
    setExchangeQuantities({});
    setExchangeInputEnabled({});
    setSubmitError("");
    setSubmitSuccess("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
          <div className="col-span-12">
            <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">Add Stock IN</div>
              <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                <Button variant="outline-secondary" onClick={handleBack}>
                  <Lucide icon="ArrowLeft" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Back to list
                </Button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="mt-3.5 flex flex-col gap-4">
              {/* Delivery Order details – single white container */}
              <div className="box p-4">
                <div className="flex flex-nowrap items-center gap-4 pb-3 border-b border-slate-200/60 dark:border-darkmode-400">
                  <span className="text-[0.94rem] font-medium shrink-0">Stock IN Details</span>
                </div>
                {submitError && (
                  <div className="mt-3 flex items-center gap-2 rounded-[0.6rem] border border-danger/30 bg-danger/5 px-4 py-2.5 text-danger">
                    <Lucide icon="AlertTriangle" className="w-6 h-6 shrink-0" />
                    {submitError}
                  </div>
                )}
                {submitSuccess && (
                  <div className="mt-3 flex items-center gap-2 rounded-[0.6rem] border border-success/30 bg-success/5 px-4 py-2.5 text-success">
                    <Lucide icon="CheckCircle" className="w-6 h-6 shrink-0" />
                    {submitSuccess}
                  </div>
                )}

                <div className="mt-3 flex flex-wrap items-end gap-4">
                      <div className="flex items-center gap-2">
                        <label className="shrink-0 text-sm">
                          Distributor
                          <span className="text-danger ml-0.5">*</span>
                        </label>
                        <FormSelect
                          value={distributorId}
                          onChange={(e) => setDistributorId(e.target.value)}
                          className="min-w-[200px] rounded-[0.5rem]"
                          required
                        >
                          <option value="">Select distributor...</option>
                          {distributors.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                        </FormSelect>
                        {loadingDistributors && <Lucide icon="Loader2" className="w-4 h-4 animate-spin text-slate-500" />}
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="shrink-0 text-sm">Date</label>
                        <div className="relative inline-flex">
                          <Lucide
                            icon="Calendar"
                            className="absolute group-[.mode--light]:!text-slate-800 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                          />
                          <Litepicker
                            value={receivedAt}
                            onChange={(e) => setReceivedAt(e.target.value)}
                            options={{
                              autoApply: true,
                              singleMode: true,
                              showWeekNumbers: false,
                              dropdowns: {
                                minYear: 2020,
                                maxYear: null,
                                months: true,
                                years: true,
                              },
                              format: "YYYY-MM-DD",
                            }}
                            className="w-full min-w-[140px] max-w-[160px] pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-300/60 shadow-sm group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-800 dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!border-darkmode-400 dark:!box text-sm"
                          />
                        </div>
                      </div>
                    <div className="flex items-center gap-2">
                      <label className="shrink-0 text-sm">
                        Invoice No.<span className="text-danger ml-0.5">*</span>
                      </label>
                      <FormInput
                        type="text"
                        placeholder="Enter invoice no."
                        value={invoiceNo}
                        onChange={(e) => setInvoiceNo(e.target.value)}
                        className="min-w-[140px] w-40"
                        required
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="shrink-0 text-sm">Upload File <span className="text-danger">*</span></label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,application/pdf,image/*,.doc,.docx"
                        onChange={handleDocumentChange}
                        className="text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700"
                      />
                      {uploadingDoc && (
                        <span className="flex items-center text-sm text-slate-500">
                          <Lucide icon="Loader2" className="w-4 h-4 animate-spin mr-1" />
                          Uploading...
                        </span>
                      )}
                      {documentUrl && (
                        <>
                          <span className="text-sm text-success flex items-center">
                            <Lucide icon="CheckCircle" className="w-4 h-4 mr-1" />
                            Document uploaded
                          </span>
                          <button
                            type="button"
                            onClick={handleRemoveDocument}
                            className="text-sm text-slate-500 hover:text-danger underline ml-2"
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>

                    <div className="flex gap-3 ml-auto shrink-0">
                      <Button type="button" variant="outline-secondary" onClick={handleReset} disabled={submitting}>
                        Reset
                      </Button>
                      <Button type="submit" variant="primary" disabled={submitting || loadingData} className="!bg-[#2D2926] hover:!bg-[#2D2926]/90 shrink-0">
                        {submitting && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                        Submit
                      </Button>
                    </div>
                </div>
                {docError && <div className="mt-3 text-danger text-xs">{docError}</div>}
              </div>

              {/* Product types – single white container, order: Devices, Refills, Accessories, then others */}
              {showProductSection && (
                <div className="box overflow-hidden">
                  {loadingData ? (
                    <div className="p-6 flex justify-center">
                      <Lucide icon="Loader2" className="w-8 h-8 animate-spin text-slate-500" />
                    </div>
                  ) : orderedProductTypes.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">No products found. Add products and product types first.</div>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center justify-end gap-3 p-4 pb-3 border-b border-slate-200/60 dark:border-darkmode-400">
                        <Menu className="relative inline-block">
                          <Menu.Button className="relative inline-flex items-center min-w-[100px] w-auto pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm whitespace-nowrap hover:bg-slate-50 dark:hover:bg-darkmode-700">
                            <Lucide
                              icon="Filter"
                              className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                            />
                            Filter
                            {selectedProductTypeFilters.length > 0 && (
                              <span className="ml-1 text-xs">({selectedProductTypeFilters.length})</span>
                            )}
                          </Menu.Button>
                          <Menu.Items className="w-64 z-[100]" style={{ zIndex: 9999 }}>
                            <div className="p-4">
                              <div className="text-sm font-medium mb-3">Product Type</div>
                              <div className="flex items-center justify-between mb-2">
                                {orderedProductTypes.length > 0 && (
                                  <button
                                    type="button"
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                    onClick={() => {
                                      const allSelected = orderedProductTypes.every(([, v]) =>
                                        selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === v.label.trim().toLowerCase())
                                      );
                                      if (allSelected) {
                                        setSelectedProductTypeFilters([]);
                                      } else {
                                        setSelectedProductTypeFilters(orderedProductTypes.map(([, v]) => v.label));
                                      }
                                    }}
                                  >
                                    {orderedProductTypes.every(([, v]) =>
                                      selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === v.label.trim().toLowerCase())
                                    )
                                      ? "Deselect all"
                                      : "Select all"}
                                  </button>
                                )}
                              </div>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {orderedProductTypes.length === 0 ? (
                                  <div className="text-xs text-slate-400">No product types available</div>
                                ) : (
                                  orderedProductTypes.map(([id, { label }]) => (
                                    <label key={id} className="flex items-center cursor-pointer gap-2">
                                      <input
                                        type="checkbox"
                                        className="form-check-input rounded border-slate-300"
                                        checked={selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === label.trim().toLowerCase())}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedProductTypeFilters([...selectedProductTypeFilters, label]);
                                          } else {
                                            setSelectedProductTypeFilters(
                                              selectedProductTypeFilters.filter((s) => s.trim().toLowerCase() !== label.trim().toLowerCase())
                                            );
                                          }
                                        }}
                                      />
                                      <span className="text-xs">{label}</span>
                                    </label>
                                  ))
                                )}
                              </div>
                              {selectedProductTypeFilters.length > 0 && (
                                <button
                                  type="button"
                                  className="w-full mt-3 px-3 py-1.5 text-xs bg-slate-200 dark:bg-darkmode-700 hover:bg-slate-300 dark:hover:bg-darkmode-600 rounded"
                                  onClick={() => setSelectedProductTypeFilters([])}
                                >
                                  Clear Filters
                                </button>
                              )}
                            </div>
                          </Menu.Items>
                        </Menu>
                        <Menu className="relative inline-block">
                          <Menu.Button className="relative inline-flex items-center min-w-[100px] w-auto pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm whitespace-nowrap hover:bg-slate-50 dark:hover:bg-darkmode-700">
                            <Lucide
                              icon="ArrowUpDown"
                              className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                            />
                            Sort
                            {productTypeCustomOrder.length > 0 && (
                              <span className="ml-1 text-xs">(Custom)</span>
                            )}
                          </Menu.Button>
                          <Menu.Items className="w-64 z-[100]" style={{ zIndex: 9999 }}>
                            <div className="p-4">
                              <div className="text-sm font-medium mb-3">Display order</div>
                              <div className="space-y-1 max-h-48 overflow-y-auto">
                                {sortMenuOrder.length === 0 ? (
                                  <div className="text-xs text-slate-400">No product types available</div>
                                ) : (
                                  sortMenuOrder.map((label, index) => (
                                    <div
                                      key={label}
                                      className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-slate-100 dark:hover:bg-darkmode-600"
                                    >
                                      <span className="text-xs truncate flex-1">
                                        {index + 1}. {label}
                                      </span>
                                      <div className="flex items-center shrink-0 gap-0.5">
                                        <button
                                          type="button"
                                          className="p-1 rounded hover:bg-slate-200 dark:hover:bg-darkmode-500 disabled:opacity-40 disabled:pointer-events-none"
                                          title="Move up"
                                          disabled={index === 0}
                                          onClick={() => moveSortOrder(index, "up")}
                                        >
                                          <Lucide icon="ChevronUp" className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          className="p-1 rounded hover:bg-slate-200 dark:hover:bg-darkmode-500 disabled:opacity-40 disabled:pointer-events-none"
                                          title="Move down"
                                          disabled={index === sortMenuOrder.length - 1}
                                          onClick={() => moveSortOrder(index, "down")}
                                        >
                                          <Lucide icon="ChevronDown" className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                              {productTypeCustomOrder.length > 0 && (
                                <button
                                  type="button"
                                  className="w-full mt-3 px-3 py-1.5 text-xs bg-slate-200 dark:bg-darkmode-700 hover:bg-slate-300 dark:hover:bg-darkmode-600 rounded"
                                  onClick={() => setProductTypeCustomOrder([])}
                                >
                                  Reset to default order
                                </button>
                              )}
                            </div>
                          </Menu.Items>
                        </Menu>
                      </div>
                      {filteredAndSortedProductTypes.length === 0 ? (
                        <div className="p-6 text-center text-slate-500">No product types match the filter.</div>
                      ) : (
                    filteredAndSortedProductTypes.map(([typeId, { label, products: prods }]) => {
                      const isExpanded = expandedTypeIds.has(typeId);
                      return (
                        <div key={typeId} className="border-t border-slate-200/60 dark:border-darkmode-400 first:border-t-0">
                          <button
                            type="button"
                            className="flex items-center gap-3 w-full text-left py-3 px-4 bg-slate-200 dark:bg-darkmode-600 hover:bg-slate-300/80 dark:hover:bg-darkmode-500 transition-colors"
                            onClick={() => toggleType(typeId)}
                            aria-expanded={isExpanded}
                          >
                            <div className="text-[0.94rem] font-medium">{label}</div>
                            <Lucide
                              icon={isExpanded ? "ChevronUp" : "ChevronDown"}
                              className="w-5 h-5 stroke-[1.3] ml-auto text-slate-500 shrink-0"
                            />
                          </button>
                          {isExpanded && (
                            <div className="pl-12 pr-12 pb-4 pt-0 border-t border-slate-200/60 dark:border-darkmode-400">
                              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,3fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)] gap-x-2 gap-y-0 items-center py-1 border-b border-slate-200/40 dark:border-darkmode-400/60 text-xs font-medium text-slate-500">
                                <div>SKU Item</div>
                                <div>Product Name</div>
                                <div className="text-center">Selling Price</div>
                                <div className="text-center">Stock IN</div>
                                <div className="text-center">Stock EXCHANGE</div>
                              </div>
                              {prods.map((p) => (
                                <div
                                  key={p.id}
                                  className="grid grid-cols-[minmax(0,1fr)_minmax(0,3fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)] gap-x-2 gap-y-0 items-center py-1 border-b border-slate-200/40 dark:border-darkmode-400/60 last:border-b-0"
                                >
                                  <div className="text-sm truncate">{p.sku ?? "—"}</div>
                                  <div className="text-sm font-medium truncate">{p.name}</div>
                                  <div className="text-sm text-center text-slate-600 dark:text-slate-400 tabular-nums">
                                    {p.retailSellingPrice != null ? Number(p.retailSellingPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                                  </div>
                                  <div className="flex justify-center items-center">
                                    <FormInput
                                      type="number"
                                      min={0}
                                      step={1}
                                      placeholder="0"
                                      className="w-full max-w-24 min-w-0"
                                      value={quantities[p.id] ?? ""}
                                      onChange={(e) => setQuantity(p.id, parseInt(e.target.value, 10) || 0)}
                                    />
                                  </div>
                                  <div className="flex justify-center items-center">
                                    <div className="flex items-center gap-1 w-full max-w-32 min-w-0">
                                      <Button
                                        type="button"
                                        variant="outline-secondary"
                                        className="h-[34px] px-2 text-[10px] leading-none"
                                        onClick={() => toggleExchangeInput(p.id)}
                                      >
                                        {exchangeInputEnabled[p.id] ? "Off" : "Enable"}
                                      </Button>
                                      <FormInput
                                        type="number"
                                        min={0}
                                        step={1}
                                        placeholder="0"
                                        className="w-full max-w-20 min-w-0"
                                        value={exchangeQuantities[p.id] ?? ""}
                                        onChange={(e) => setExchangeQuantity(p.id, parseInt(e.target.value, 10) || 0)}
                                        disabled={!exchangeInputEnabled[p.id]}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                      )}
                    </>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}
